import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";

export default function PromoFilm() {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "center center"] });
  const scale = useTransform(scrollYProgress, [0, 1], [0.9, 1]);

  const togglePlay = () => {
    const v = videoRef.current;
    v.paused ? v.play() : v.pause();
  };
  const toggleMute = () => {
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  return (
    <section ref={sectionRef} id="film" data-testid="promo-film-section" className="relative bg-midnight py-32 sm:py-44 overflow-hidden">
      <div className="absolute top-0 left-1/3 w-[560px] h-[560px] rounded-full bg-blush/10 blur-[140px]" aria-hidden="true" />
      <div className="max-w-[1200px] mx-auto px-6 sm:px-10 relative grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
        <div>
          <FadeUp className="mb-6">
            <p className="text-[12px] tracking-[0.45em] uppercase text-gold">The Film</p>
          </FadeUp>
          <h2 className="font-serif font-light text-ivory leading-[1.06] tracking-tight text-4xl sm:text-5xl mb-10">
            <MaskedLines inView lines={[<>What our Bridgerton</>, <>Actors <span className="italic text-blush">have to say:</span></>]} />
          </h2>
          <FadeUp delay={0.15}>
            <p className="text-ivory/60 text-base max-w-md leading-relaxed">
              Thirty-four seconds of pure fantasy — our 2026 promo, starring
              a cat, a dog, and the floors that make every guest look twice.
            </p>
          </FadeUp>
          <FadeUp delay={0.25} className="mt-10">
            <div className="flex items-center gap-4">
              <span className="w-10 h-px bg-gold/60" />
              <p className="text-[11px] tracking-[0.35em] uppercase text-gold/80">2026 Promo · Sound on</p>
            </div>
          </FadeUp>
        </div>

        <motion.div
          style={{ scale }}
          className="relative w-full max-w-[400px] mx-auto rounded-[2rem] overflow-hidden shadow-[0_60px_120px_-40px_rgba(240,107,168,0.4)] ring-1 ring-ivory/15"
        >
          <video
            ref={videoRef}
            data-testid="promo-video"
            src="/assets/promo/fantasy-promo.mp4"
            poster="/assets/promo/poster.jpg"
            className="w-full aspect-[9/16] object-cover cursor-pointer"
            loop
            playsInline
            preload="metadata"
            onClick={togglePlay}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
          />
          {!playing && (
            <button
              data-testid="promo-center-play"
              onClick={togglePlay}
              aria-label="Play film"
              className="absolute inset-0 m-auto w-20 h-20 rounded-full bg-ivory/20 backdrop-blur-xl border border-ivory/40 text-ivory flex items-center justify-center hover:bg-ivory/35 hover:scale-110 transition-[background-color,transform] duration-300"
            >
              <Play size={28} className="ml-1" />
            </button>
          )}
          <div className="absolute bottom-5 right-5 flex gap-3">
            <button
              data-testid="promo-play-toggle"
              onClick={togglePlay}
              aria-label={playing ? "Pause film" : "Play film"}
              className="w-12 h-12 rounded-full bg-ivory/15 backdrop-blur-xl border border-ivory/25 text-ivory flex items-center justify-center hover:bg-ivory/30 transition-colors duration-300"
            >
              {playing ? <Pause size={17} /> : <Play size={17} className="ml-0.5" />}
            </button>
            <button
              data-testid="promo-sound-toggle"
              onClick={toggleMute}
              aria-label={muted ? "Unmute film" : "Mute film"}
              className="w-12 h-12 rounded-full bg-ivory/15 backdrop-blur-xl border border-ivory/25 text-ivory flex items-center justify-center hover:bg-ivory/30 transition-colors duration-300"
            >
              {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
